import type { CSSProperties, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiBlob } from '../api/cliente';

/**
 * A wiki é interna: o servidor exige sessão até para LER imagem, e `<img src>` /
 * `background-image` não enviam o token. Este hook baixa o arquivo via fetch
 * autenticado e devolve um object URL para o CSS usar.
 *
 * O cache é infinito de propósito — o nome do arquivo é um UUID imutável, então
 * a mesma URL nunca muda de conteúdo. Os object URLs vivem até o reload da SPA,
 * o que para miniaturas de wiki é um custo de memória irrelevante.
 */
export function useImagemAutenticada(url?: string | null): string | undefined {
  const { data } = useQuery({
    queryKey: ['arquivo', url],
    queryFn: async () => URL.createObjectURL(await apiBlob(url!)),
    enabled: Boolean(url),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data;
}

/**
 * Um `<span>` de capa/miniatura cujo background é a imagem autenticada.
 * Substitui os `style={{ backgroundImage }}` diretos das listagens — que dentro
 * de um `.map()` não poderiam chamar o hook.
 *
 * <p>`mini` pede ao servidor a versão reduzida, que o upload já gerou. Vale em toda
 * grade e em todo chip de vínculo: ali a imagem é desenhada com menos de 100px de
 * lado, e baixar o original para isso é o que fazia uma página de itens com quarenta
 * fichas pesar dezenas de megabytes. Onde a miniatura não existe — imagem pequena
 * demais para valer uma — o servidor devolve o original, então ligar o `mini` nunca
 * deixa a capa vazia.
 */
/**
 * Um `<img>` de origem autenticada — a miniatura dos anexos de tarefa e de conversa.
 *
 * <p>Existe pela mesma razão da capa: `<img src>` cru não manda o token, e o servidor
 * responde 401 no lugar da imagem. Enquanto o blob desce, nada aparece — o quadrado
 * vazio do carregamento é preferível a um ícone de imagem quebrada.
 */
export function ImagemAutenticada({
  url,
  alt,
  className,
}: {
  url?: string | null;
  alt: string;
  className?: string;
}) {
  const imagem = useImagemAutenticada(url);
  return imagem ? <img src={imagem} alt={alt} className={className} /> : null;
}

export function CapaAutenticada({
  url,
  className,
  style,
  title,
  mini,
  children,
}: {
  url?: string | null;
  className: string;
  style?: CSSProperties;
  title?: string;
  mini?: boolean;
  children?: ReactNode;
}) {
  const imagem = useImagemAutenticada(url && mini ? `${url}?mini=true` : url);
  return (
    <span
      className={className}
      title={title}
      style={imagem ? { ...style, backgroundImage: `url('${imagem}')` } : style}
    >
      {children}
    </span>
  );
}
